/**
 * 指文字ドリルの出題対象・シャッフル・答えの判定。
 * 画面から切り離した純粋な関数だけを置く。
 */
import {
  FINGERSPELLING_ENTRIES,
  type FingerspellingEntry,
} from "./fingerspelling";

/** make = つくる（かな → 指文字）、read = よむ（指文字 → かな） */
export type DrillMode = "make" | "read";

/** ドリル1回の問題数 */
export const DRILL_QUESTION_COUNT = 10;

/** よむでは、キーボードで入力しにくい濁点・半濁点・小さい文字を出題しない */
const EXCLUDED_FROM_READ: ReadonlySet<string> = new Set([
  "dakuten",
  "handakuten",
  "small-ya",
  "small-yu",
  "small-yo",
  "small-tsu",
]);

export const getDrillPool = (
  mode: DrillMode,
): readonly FingerspellingEntry[] =>
  mode === "make"
    ? FINGERSPELLING_ENTRIES
    : FINGERSPELLING_ENTRIES.filter(
        (entry) => !EXCLUDED_FROM_READ.has(entry.id),
      );

/** 重複なしで count 個をシャッフルして選ぶ。元の配列は変更しない */
export const pickQuestions = (
  pool: readonly FingerspellingEntry[],
  count: number,
  random: () => number = Math.random,
): FingerspellingEntry[] => {
  const remaining = [...pool];
  const picked: FingerspellingEntry[] = [];
  while (picked.length < count && remaining.length > 0) {
    const index = Math.min(
      Math.floor(random() * remaining.length),
      remaining.length - 1,
    );
    const [entry] = remaining.splice(index, 1);
    if (entry) picked.push(entry);
  }
  return picked;
};

const KATAKANA_TO_HIRAGANA_OFFSET = 0x60;

/** 答えの比較用にそろえる: 全角・半角を統一し、カタカナをひらがなに、ハイフン類を長音にする */
export const normalizeAnswer = (input: string): string =>
  input
    .normalize("NFKC")
    .trim()
    .replace(/[ァ-ヶ]/g, (char) =>
      String.fromCharCode(char.charCodeAt(0) - KATAKANA_TO_HIRAGANA_OFFSET),
    )
    .replace(/[-‐-―−]/g, "ー");

/** 正規化したあとの完全一致（末尾一致や部分一致は使わない） */
export const isCorrectAnswer = (
  input: string,
  entry: FingerspellingEntry,
): boolean => normalizeAnswer(input) === entry.kana;
